import { useCallback } from 'react';
import type { FieldValues, UseFormSetValue, UseFormWatch } from 'react-hook-form';
import {
  updateProRateCents,
  getSourceAndLabel,
  calculateNewBaseMarginPercentW2,
} from '../PositionRateSettingsForm.utils';
import { DisplayHelpers } from 'utils/helpers';

type UseChargeRateEffectProps = {
  source: string;
  inheritedSettings: any;
  watchedFields: {
    chargeRateCents: number;
    blendedRateCents: number;
    blendedRateMarkup: number | null | undefined;
    useBlendedRate: boolean;
    marginPercent: number | undefined;
    proRateCents: number;
  };
  mergedSettings: any;
  setValue: UseFormSetValue<FieldValues>;
  watch: UseFormWatch<FieldValues>;
  isW2Local: boolean;
  validateBaseMarginPercentW2?: (value: number) => void;
};

export const useChargeRateEffect = ({
  source,
  inheritedSettings,
  watchedFields,
  mergedSettings,
  setValue,
  watch,
  isW2Local,
  validateBaseMarginPercentW2,
}: UseChargeRateEffectProps) => {
  const handleChargeRateChange = useCallback(
    (value: string | number) => {
      const chargeRateCents =
        typeof value === 'string' ? DisplayHelpers.stringToCents(value) : value;

      const blendedRateCents = Math.round(
        chargeRateCents * (1 + (watchedFields.blendedRateMarkup || 0)),
      );

      if (
        watchedFields.blendedRateMarkup !== null &&
        watchedFields.blendedRateMarkup !== undefined &&
        watchedFields.blendedRateMarkup >= 0
      ) {
        setValue(`${source}.blended_rate_cents`, blendedRateCents);
      }

      if (watchedFields.marginPercent) {
        const isLocationLevel = source.includes('location');
        const applicableRate =
          !isLocationLevel && watchedFields.useBlendedRate ? blendedRateCents : chargeRateCents;

        updateProRateCents({
          setValue,
          watch,
          source,
          baseMarginPercent: watchedFields.marginPercent,
          rateCents: applicableRate,
          inheritedSettings,
        });
      }
    },
    [
      watchedFields.blendedRateMarkup,
      watchedFields.useBlendedRate,
      watchedFields.marginPercent,
      source,
      setValue,
      watch,
      inheritedSettings,
    ],
  );

  const handleBlendedRateChange = useCallback(
    (value: string | number) => {
      const blendedRateCents =
        typeof value === 'string' ? DisplayHelpers.stringToCents(value) : value;

      const chargeRateCents = mergedSettings.charge_rate_cents || watchedFields.chargeRateCents;
      const blendedRateMarkup = blendedRateCents / chargeRateCents - 1;
      const blendedRateMarkupRounded = Math.round(blendedRateMarkup * 10000) / 10000;

      const isBlendedMarkupValid = blendedRateMarkupRounded >= 0;
      if (isBlendedMarkupValid) {
        setValue(`${source}.blended_rates.blended_rate_markup`, blendedRateMarkupRounded);
      }

      if (watchedFields.useBlendedRate && isBlendedMarkupValid && watchedFields.marginPercent) {
        updateProRateCents({
          setValue,
          watch,
          source,
          baseMarginPercent: watchedFields.marginPercent,
          rateCents: blendedRateCents,
          inheritedSettings,
        });
      }
    },
    [
      mergedSettings.charge_rate_cents,
      watchedFields.chargeRateCents,
      watchedFields.useBlendedRate,
      watchedFields.marginPercent,
      source,
      setValue,
      watch,
      inheritedSettings,
    ],
  );

  const handleProfessionalRateChange = useCallback(
    (value: string | number) => {
      const professionalRate =
        typeof value === 'string' ? DisplayHelpers.stringToCents(value) : value;

      const isLocationLevel = source.includes('location');

      let rateCents = mergedSettings.charge_rate_cents || watchedFields.chargeRateCents;

      if (watchedFields.useBlendedRate && !isLocationLevel) {
        rateCents = mergedSettings.blended_rate_cents || watchedFields.blendedRateCents;
      }

      const newMargin = (1 - professionalRate / rateCents).toFixed(5);

      const { field } = getSourceAndLabel(source);
      setValue(`${source}.${field}`, newMargin);

      if (source.includes('w2.local')) {
        const baseMarginPercentW2 = calculateNewBaseMarginPercentW2({
          setValue,
          source,
          watch,
          totalMarginPercentW2: Number(newMargin),
        });
        return baseMarginPercentW2;
      }
      return null;
    },
    [mergedSettings, watchedFields, source, setValue, watch],
  );

  const handleProfessionalRateChangeWithValidation = useCallback(
    (value: string | number) => {
      const baseMarginPercentW2 = handleProfessionalRateChange(value);

      if (isW2Local && validateBaseMarginPercentW2 && baseMarginPercentW2 !== null) {
        validateBaseMarginPercentW2(baseMarginPercentW2);
      }
    },
    [handleProfessionalRateChange, isW2Local, validateBaseMarginPercentW2],
  );

  return {
    handleChargeRateChange,
    handleBlendedRateChange,
    handleProfessionalRateChange: handleProfessionalRateChangeWithValidation,
  };
};
